import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { caseStudyBackdropUrl, realCaseStudies } from "@/components/equinox/data";

export default function CaseStudiesPage() {
  return (
    <section className="px-6 pb-20 pt-16 sm:px-8 lg:px-12 lg:pb-28 lg:pt-20">
      <div className="mx-auto max-w-7xl">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
          <p className="section-kicker">Case Studies</p>
          <h1 className="section-heading mt-5 max-w-[12ch] text-white">Proven app growth work across ratings, reviews, and organic installs.</h1>
          <p className="section-copy mt-6 text-white/[0.64]">
            Premium proof cards showing the challenge, what Equinox did, and the verified public results we can responsibly show.
          </p>
        </motion.div>

        <div className="mt-14 grid gap-6 md:grid-cols-8 xl:grid-cols-12">
          {realCaseStudies.map((study, index) => (
            <motion.div
              key={study.slug}
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.55, delay: index * 0.06 }}
              className={index % 3 === 0 ? "md:col-span-8 xl:col-span-7" : "md:col-span-4 xl:col-span-5"}
            >
              <Link
                to={`/case-studies/${study.slug}`}
                className="editorial-shell glitter-card glass-panel cinematic-card block overflow-hidden rounded-[2rem] p-6 lg:p-7"
                data-testid={`case-study-link-${study.slug}`}
              >
                <div className="relative mb-6 overflow-hidden rounded-[1.4rem] border border-white/[0.08]">
                  <img src={caseStudyBackdropUrl} alt={`${study.client} case study`} className="aspect-[16/9] w-full object-cover opacity-70" data-testid={`case-study-image-${study.slug}`} />
                  <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(3,4,8,0.05),rgba(3,4,8,0.82))]" />
                </div>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-[0.26em] text-white/[0.42]">{study.type} • {study.client}</p>
                    <h2 className="mt-3 font-display text-3xl text-white">{study.headline}</h2>
                  </div>
                  <ArrowRight className="mt-1 h-5 w-5 text-blue-200" />
                </div>

                <p className="mt-5 text-sm leading-8 text-white/[0.65]">{study.summary}</p>

                <div className="mt-6 grid gap-3 md:grid-cols-2">
                  <div className="rounded-[1.3rem] border border-white/[0.08] bg-black/20 p-4">
                    <p className="text-xs uppercase tracking-[0.2em] text-white/[0.4]">Challenge</p>
                    <p className="mt-3 text-sm leading-7 text-white/[0.72]">{study.problem}</p>
                  </div>
                  <div className="rounded-[1.3rem] border border-white/[0.08] bg-black/20 p-4">
                    <p className="text-xs uppercase tracking-[0.2em] text-white/[0.4]">What Equinox Did</p>
                    <p className="mt-3 text-sm leading-7 text-white/[0.72]">{study.solution}</p>
                  </div>
                </div>

                <div className="mt-6">
                  <p className="text-xs uppercase tracking-[0.2em] text-white/[0.4]">Results</p>
                </div>

                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {study.numbers.map((number) => (
                    <div key={number} className="rounded-[1.25rem] border border-white/[0.08] bg-white/[0.04] p-4">
                      <p className="font-display text-2xl text-white">{number}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-5 rounded-[1.3rem] border border-white/[0.08] bg-black/20 p-4">
                  <div className="space-y-3 text-sm text-white/[0.72]">
                    {study.results.map((result) => (
                      <div key={result}>{result}</div>
                    ))}
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}